/**
 * htmlToTable.js
 *
 * Converts hierarchical HTML (with <details>/<summary> or heading hierarchy)
 * and Markdown files into the app's TableData JSON format for import.
 *
 * Mapping rules:
 *   - Document title → table name
 *   - Preamble text → table description
 *   - Top-level sections → rows (section name = row header)
 *   - Paragraph text (NOT lists) → cell in column 1
 *   - Lists (ul/ol) → subtables: bold lead = row header, rest = "Details" column
 *   - HTML <table> elements → subtables with proper columns
 *   - Child <details> sections → subtables (recursive, same rules)
 */

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

let _idCounter = 0;
function genId() {
  _idCounter++;
  return `imp-${Date.now()}-${_idCounter}-${Math.random().toString(36).substr(2, 6)}`;
}

/** Shared empty-field defaults for SubTableData */
function subtableDefaults() {
  return {
    expanded: true,
    row_subtables: {},
    row_heights: null,
    row_header_width: null,
    header_rows: null,
    header_rows_merged: null,
    header_cols: null,
    header_cols_merged: null,
    sort_column: null,
    sort_direction: null,
    keep_done_at_bottom: false,
    done_column: null,
    column_border_styles: null,
    row_border_styles: null,
    translation_columns: null,
  };
}

// ---------------------------------------------------------------------------
// Text extraction — paragraphs only (skips lists, tables, details)
// ---------------------------------------------------------------------------

/**
 * Extract paragraph / block-level text from a container.
 * Skips <ul>, <ol>, <table>, <details> — those become their own subtables.
 */
function extractParagraphText(container) {
  const parts = [];

  for (const node of container.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent.trim();
      if (t) parts.push(t);
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const tag = node.tagName.toLowerCase();

    // Skip elements that become their own subtables
    if (tag === 'details' || tag === 'table' || tag === 'ul' || tag === 'ol') continue;

    if (tag === 'p') {
      const t = node.textContent.trim();
      if (t) parts.push(t);
    } else if (tag === 'blockquote') {
      parts.push('> ' + node.textContent.trim());
    } else if (tag === 'pre') {
      parts.push(node.textContent.trim());
    } else if (tag === 'hr') {
      parts.push('---');
    } else if (/^h[1-6]$/.test(tag)) {
      const t = node.textContent.trim();
      if (t) parts.push(t);
    } else if (tag === 'div') {
      const inner = extractParagraphText(node);
      if (inner) parts.push(inner);
    }
    // ignore other inline elements at this level
  }

  return parts.join('\n\n');
}

// ---------------------------------------------------------------------------
// List → SubTableData (bold lead = row header, rest = Details column)
// ---------------------------------------------------------------------------

/**
 * Try to derive a subtable name from the paragraph text immediately
 * preceding a list. If the text ends with ":" we use the clause before it.
 */
function deriveListName(precedingText) {
  if (!precedingText) return null;
  const trimmed = precedingText.trim();
  if (!trimmed.endsWith(':')) return null;

  // Take the last sentence / clause before the colon
  const withoutColon = trimmed.slice(0, -1).trim();
  // Try to grab the bit after the last period or the whole thing
  const lastPeriod = withoutColon.lastIndexOf('.');
  let clause = lastPeriod >= 0
    ? withoutColon.slice(lastPeriod + 1).trim()
    : withoutColon;

  // Cap at 60 chars
  if (clause.length > 60) {
    clause = clause.slice(0, 57) + '...';
  }
  // Capitalize first letter
  return clause.charAt(0).toUpperCase() + clause.slice(1);
}

/**
 * Parse a <li> element into { header, detail }.
 * If the li starts with <strong>, that becomes the header and the rest is the detail.
 * Otherwise header is null and the full text is the detail.
 */
function parseLiItem(li) {
  const strong = li.querySelector(':scope > strong');
  if (strong) {
    let header = strong.textContent.trim();
    // Clean trailing separators: " — ", " - ", ":", "."
    header = header.replace(/[\s\u2014\u2013—–\-:.,]+$/, '').trim();

    // Get the rest of the text (everything that's NOT in the <strong>)
    let detail = '';
    let pastStrong = false;
    for (const node of li.childNodes) {
      if (node === strong || (node.nodeType === Node.ELEMENT_NODE && node.contains(strong))) {
        pastStrong = true;
        continue;
      }
      if (pastStrong || node !== strong) {
        if (pastStrong) {
          detail += node.textContent;
        }
      }
    }
    detail = detail.trim();
    // If detail extraction failed, fall back to removing strong text
    if (!detail) {
      detail = li.textContent.replace(strong.textContent, '').trim();
    }
    return { header, detail: detail || null };
  }

  // No bold lead — return full text
  return { header: null, detail: li.textContent.trim() || null };
}

/**
 * Convert a <ul> or <ol> element into a SubTableData.
 * Bold-lead items get their bold text as the row header.
 * Items without bold get numbered row headers.
 */
function listToSubtable(listEl, name) {
  const items = Array.from(listEl.querySelectorAll(':scope > li'));
  const rows = [];
  const rowHeaders = [];

  for (let i = 0; i < items.length; i++) {
    const { header, detail } = parseLiItem(items[i]);
    rowHeaders.push(header || String(i + 1));
    rows.push([detail]);
  }

  // Compute a reasonable row header width from the longest header
  const maxHeaderLen = Math.max(...rowHeaders.map(h => h.length), 5);
  const headerWidth = Math.min(Math.max(80, maxHeaderLen * 7 + 20), 350);

  return {
    id: genId(),
    name: name || '',
    rows,
    num_rows: rows.length,
    num_cols: 1,
    ...subtableDefaults(),
    column_widths: [500],
    row_header_width: headerWidth,
    show_headers: true,
    column_headers: ['Details'],
    show_row_headers: true,
    row_headers: rowHeaders,
  };
}

// ---------------------------------------------------------------------------
// HTML <table> → SubTableData
// ---------------------------------------------------------------------------

function htmlTableToSubtable(tableEl, name) {
  const headers = [];
  const rows = [];

  const thead = tableEl.querySelector('thead');
  if (thead) {
    const firstRow = thead.querySelector('tr');
    if (firstRow) {
      for (const th of firstRow.querySelectorAll('th')) {
        headers.push(th.textContent.trim());
      }
    }
  }

  const bodyRows = tableEl.querySelectorAll('tbody > tr');
  const rowSource = bodyRows.length > 0 ? bodyRows : tableEl.querySelectorAll('tr');
  for (const tr of rowSource) {
    const cells = tr.querySelectorAll('td');
    if (cells.length === 0) continue;
    const row = Array.from(cells).map(td => {
      const t = td.textContent.trim();
      return t || null;
    });
    rows.push(row);
  }

  const numCols = Math.max(headers.length, ...rows.map(r => r.length), 1);

  const paddedRows = rows.map(r => {
    const padded = [...r];
    while (padded.length < numCols) padded.push(null);
    return padded;
  });

  const colWidths = [];
  for (let c = 0; c < numCols; c++) {
    let maxLen = headers[c] ? headers[c].length : 5;
    for (const row of paddedRows) {
      const cellLen = row[c] ? String(row[c]).length : 0;
      if (cellLen > maxLen) maxLen = cellLen;
    }
    colWidths.push(Math.min(Math.max(120, maxLen * 6 + 40), 600));
  }

  return {
    id: genId(),
    name: name || 'Data',
    rows: paddedRows,
    num_rows: paddedRows.length,
    num_cols: numCols,
    ...subtableDefaults(),
    column_widths: colWidths,
    show_headers: headers.length > 0,
    column_headers: headers.length > 0 ? headers : null,
    show_row_headers: false,
    row_headers: null,
  };
}

// ---------------------------------------------------------------------------
// Collect subtables from a section body (lists, tables, child details)
// ---------------------------------------------------------------------------

/**
 * Walk a container's children IN ORDER and build subtables from lists,
 * HTML tables, and child details. Also returns the paragraph-only text.
 *
 * For list naming: uses the immediately preceding paragraph if it ends with ":".
 */
function buildSectionSubtables(container) {
  const subtables = [];
  let lastParagraphText = '';
  const textParts = [];

  for (const child of container.children) {
    const tag = child.tagName.toLowerCase();

    if (tag === 'p' || tag === 'blockquote' || tag === 'pre') {
      const t = child.textContent.trim();
      if (t) {
        lastParagraphText = t;
        textParts.push(t);
      }
    } else if (tag === 'ul' || tag === 'ol') {
      // List → subtable
      const listName = deriveListName(lastParagraphText);
      subtables.push(listToSubtable(child, listName));
      lastParagraphText = ''; // consumed
    } else if (tag === 'table') {
      subtables.push(htmlTableToSubtable(child, ''));
      lastParagraphText = '';
    } else if (tag === 'details') {
      subtables.push(detailsToSubtable(child));
      lastParagraphText = '';
    }
    // skip divs that are just wrappers (section-body is the container itself)
  }

  const cellText = textParts.join('\n\n').trim();
  return { cellText, subtables };
}

// ---------------------------------------------------------------------------
// <details> element → SubTableData (recursive)
// ---------------------------------------------------------------------------

function detailsToSubtable(detailsEl) {
  const summary = detailsEl.querySelector(':scope > summary');
  const sectionBody =
    detailsEl.querySelector(':scope > .section-body') || detailsEl;
  const name = summary ? summary.textContent.trim() : 'Section';

  // Check if this section's body has child <details> elements
  const childDetails = Array.from(
    sectionBody.querySelectorAll(':scope > details')
  );

  // If this section contains child <details>, build a multi-row subtable
  // where each child <details> becomes a row (with row header from its summary)
  // and any non-details content (paragraphs, lists) at this level is kept
  // as an intro row or ignored if empty.
  if (childDetails.length > 0) {
    // Gather any paragraph text and non-details subtables (lists, tables) at this level
    const nonDetailsSubtables = [];
    const textParts = [];
    let lastParagraphText = '';

    for (const child of sectionBody.children) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'details') continue; // handled below as rows
      if (tag === 'p' || tag === 'blockquote' || tag === 'pre') {
        const t = child.textContent.trim();
        if (t) {
          lastParagraphText = t;
          textParts.push(t);
        }
      } else if (tag === 'ul' || tag === 'ol') {
        const listName = deriveListName(lastParagraphText);
        nonDetailsSubtables.push(listToSubtable(child, listName));
        lastParagraphText = '';
      } else if (tag === 'table') {
        nonDetailsSubtables.push(htmlTableToSubtable(child, ''));
        lastParagraphText = '';
      }
    }

    const introText = textParts.join('\n\n').trim();

    const rows = [];
    const rowHeaders = [];
    const rowSubtables = {};
    let rowIdx = 0;

    // If there's intro text or non-details subtables, add an intro row
    if (introText || nonDetailsSubtables.length > 0) {
      rows.push([introText || null]);
      rowHeaders.push('Overview');
      if (nonDetailsSubtables.length > 0) {
        rowSubtables[`row-${rowIdx}`] = nonDetailsSubtables;
      }
      rowIdx++;
    }

    // Each child <details> becomes a row
    for (const childDetail of childDetails) {
      const childSummary = childDetail.querySelector(':scope > summary');
      const childBody =
        childDetail.querySelector(':scope > .section-body') || childDetail;
      const childName = childSummary
        ? childSummary.textContent.trim()
        : `Item ${rowIdx + 1}`;

      rowHeaders.push(childName);

      const { cellText: childText, subtables: childSubs } =
        buildSectionSubtables(childBody);
      rows.push([childText || null]);

      if (childSubs.length > 0) {
        rowSubtables[`row-${rowIdx}`] = childSubs;
      }
      rowIdx++;
    }

    const maxHeaderLen = Math.max(...rowHeaders.map(h => h.length), 5);
    const headerWidth = Math.min(Math.max(120, maxHeaderLen * 7 + 20), 350);

    return {
      id: genId(),
      name,
      rows,
      num_rows: rows.length,
      num_cols: 1,
      ...subtableDefaults(),
      row_subtables: rowSubtables,
      column_widths: [500],
      row_header_width: headerWidth,
      show_headers: rows.some(r => r[0]) ? true : false,
      column_headers: rows.some(r => r[0]) ? ['Content'] : null,
      show_row_headers: true,
      row_headers: rowHeaders,
    };
  }

  // No child <details> — original single-row behavior
  const { cellText, subtables } = buildSectionSubtables(sectionBody);

  // If the ONLY content is a single HTML-table subtable with no text,
  // promote it directly (cleaner nesting)
  if (
    subtables.length === 1 &&
    !cellText &&
    !subtables[0].name // unnamed table subtable
  ) {
    const promoted = subtables[0];
    promoted.name = name;
    return promoted;
  }

  // Build the subtable: one row with the text, sub-subtables attached
  const rows = [[cellText || null]];
  const rowSubtables = {};

  if (subtables.length > 0) {
    rowSubtables['row-0'] = subtables;
  }

  return {
    id: genId(),
    name,
    rows,
    num_rows: 1,
    num_cols: 1,
    ...subtableDefaults(),
    row_subtables: rowSubtables,
    column_widths: [500],
    show_headers: cellText ? true : false,
    column_headers: cellText ? ['Content'] : null,
    show_row_headers: false,
    row_headers: null,
  };
}

// ---------------------------------------------------------------------------
// Heading-based HTML parsing (fallback when no <details> found)
// ---------------------------------------------------------------------------

function buildHeadingTree(container) {
  const nodes = [];
  const stack = [];

  const elements = Array.from(container.children).filter(
    el => !el.classList.contains('toolbar') && el.tagName.toLowerCase() !== 'script'
  );

  let currentSection = null;

  for (const el of elements) {
    const tag = el.tagName.toLowerCase();
    const headingMatch = tag.match(/^h([1-6])$/);

    if (headingMatch) {
      const level = parseInt(headingMatch[1], 10);
      const title = el.textContent.trim();
      const section = { level, title, contentEls: [], children: [] };

      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop();
      }

      if (stack.length > 0) {
        stack[stack.length - 1].node.children.push(section);
      } else {
        nodes.push(section);
      }

      stack.push({ level, node: section });
      currentSection = section;
    } else if (currentSection) {
      currentSection.contentEls.push(el);
    }
  }

  return nodes;
}

function headingSectionToSubtable(section, doc) {
  const name = section.title;

  // Build temp container from this section's content elements
  const tempDiv = doc.createElement('div');
  for (const el of section.contentEls) {
    tempDiv.appendChild(el.cloneNode(true));
  }

  const { cellText, subtables: directSubtables } = buildSectionSubtables(tempDiv);

  // Add child heading sections as subtables
  const childSubtables = section.children.map(child =>
    headingSectionToSubtable(child, doc)
  );

  const allSubtables = [...directSubtables, ...childSubtables];

  // Promote if single unnamed table with no text
  if (allSubtables.length === 1 && !cellText && !allSubtables[0].name) {
    const promoted = allSubtables[0];
    promoted.name = name;
    return promoted;
  }

  const rows = [[cellText || null]];
  const rowSubtables = {};
  if (allSubtables.length > 0) {
    rowSubtables['row-0'] = allSubtables;
  }

  return {
    id: genId(),
    name,
    rows,
    num_rows: 1,
    num_cols: 1,
    ...subtableDefaults(),
    row_subtables: rowSubtables,
    column_widths: [500],
    show_headers: cellText ? true : false,
    column_headers: cellText ? ['Content'] : null,
    show_row_headers: false,
    row_headers: null,
  };
}

// ---------------------------------------------------------------------------
// Markdown → TableData
// ---------------------------------------------------------------------------

export function parseMarkdownToTableData(mdString) {
  const lines = mdString.split('\n');
  const sections = [];
  let currentSection = null;
  const preambleLines = [];

  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+)/);
    if (match) {
      if (currentSection) sections.push(currentSection);
      currentSection = {
        level: match[1].length,
        title: match[2].trim(),
        contentLines: [],
      };
    } else {
      if (currentSection) {
        currentSection.contentLines.push(line);
      } else {
        preambleLines.push(line);
      }
    }
  }
  if (currentSection) sections.push(currentSection);

  // Build tree
  const tree = [];
  const stack = [];

  for (const sec of sections) {
    const node = { ...sec, children: [] };

    while (stack.length > 0 && stack[stack.length - 1].level >= sec.level) {
      stack.pop();
    }

    if (stack.length > 0) {
      stack[stack.length - 1].children.push(node);
    } else {
      tree.push(node);
    }

    stack.push(node);
  }

  const titleSection = sections.find(s => s.level === 1);
  const tableName = titleSection ? titleSection.title : 'Imported Document';

  const topSections =
    tree.length === 1 && tree[0].level === 1 ? tree[0].children : tree;

  const preamble = preambleLines.join('\n').trim();

  const rows = [];
  const rowHeaders = [];
  const rowSubtables = {};

  for (let i = 0; i < topSections.length; i++) {
    const sec = topSections[i];
    rowHeaders.push(sec.title);

    const { paragraphText, listSubtables } = parseMdSectionContent(sec.contentLines);
    rows.push([paragraphText || null]);

    const childSubtables = sec.children.map(child => mdSectionToSubtable(child));
    const allSubtables = [...listSubtables, ...childSubtables];

    if (allSubtables.length > 0) {
      rowSubtables[`row-${i}`] = allSubtables;
    }
  }

  return {
    id: genId(),
    name: tableName,
    description: preamble || null,
    rows: rows.length > 0 ? rows : [[null]],
    num_rows: Math.max(rows.length, 1),
    num_cols: 1,
    row_subtables: rowSubtables,
    column_widths: [500],
    row_heights: null,
    row_header_width: 250,
    show_headers: true,
    column_headers: ['Content'],
    show_row_headers: true,
    row_headers: rowHeaders.length > 0 ? rowHeaders : ['Section 1'],
    sort_column: null,
    sort_direction: null,
    keep_done_at_bottom: false,
  };
}

/**
 * Parse markdown content lines into paragraph text and list subtables.
 * Lists become subtables; bold leads (**text**) become row headers.
 */
function parseMdSectionContent(contentLines) {
  const paragraphParts = [];
  const listSubtables = [];
  let currentListItems = [];
  let lastParagraph = '';
  let inList = false;

  function flushList() {
    if (currentListItems.length === 0) return;

    const rows = [];
    const rowHeaders = [];
    for (let i = 0; i < currentListItems.length; i++) {
      const item = currentListItems[i];
      // Check for bold lead: **text** or __text__
      const boldMatch = item.match(/^\*\*(.+?)\*\*[\s\u2014\u2013—–\-:.,]*\s*(.*)/s);
      const boldMatch2 = item.match(/^__(.+?)__[\s\u2014\u2013—–\-:.,]*\s*(.*)/s);
      const m = boldMatch || boldMatch2;
      if (m) {
        rowHeaders.push(m[1].trim());
        rows.push([m[2].trim() || null]);
      } else {
        rowHeaders.push(String(i + 1));
        rows.push([item]);
      }
    }

    const listName = deriveListName(lastParagraph);
    const maxHeaderLen = Math.max(...rowHeaders.map(h => h.length), 5);
    const headerWidth = Math.min(Math.max(80, maxHeaderLen * 7 + 20), 350);

    listSubtables.push({
      id: genId(),
      name: listName || '',
      rows,
      num_rows: rows.length,
      num_cols: 1,
      ...subtableDefaults(),
      column_widths: [500],
      row_header_width: headerWidth,
      show_headers: true,
      column_headers: ['Details'],
      show_row_headers: true,
      row_headers: rowHeaders,
    });

    currentListItems = [];
  }

  for (const line of contentLines) {
    const bulletMatch = line.match(/^\s*[-*+]\s+(.+)/);
    const numberedMatch = line.match(/^\s*\d+[.)]\s+(.+)/);

    if (bulletMatch || numberedMatch) {
      inList = true;
      currentListItems.push((bulletMatch || numberedMatch)[1].trim());
    } else {
      if (inList) {
        flushList();
        inList = false;
      }
      const trimmed = line.trim();
      if (trimmed) {
        lastParagraph = trimmed;
        paragraphParts.push(trimmed);
      }
    }
  }
  if (inList) flushList();

  return {
    paragraphText: paragraphParts.join('\n\n').trim(),
    listSubtables,
  };
}

function mdSectionToSubtable(section) {
  const { paragraphText, listSubtables } = parseMdSectionContent(section.contentLines);

  const childSubtables = section.children.map(child => mdSectionToSubtable(child));
  const allSubtables = [...listSubtables, ...childSubtables];

  const rows = [[paragraphText || null]];
  const rowSubtables = {};

  if (allSubtables.length > 0) {
    rowSubtables['row-0'] = allSubtables;
  }

  return {
    id: genId(),
    name: section.title,
    rows,
    num_rows: 1,
    num_cols: 1,
    ...subtableDefaults(),
    row_subtables: rowSubtables,
    column_widths: [500],
    show_headers: paragraphText ? true : false,
    column_headers: paragraphText ? ['Content'] : null,
    show_row_headers: false,
    row_headers: null,
  };
}

// ---------------------------------------------------------------------------
// Main: HTML → TableData
// ---------------------------------------------------------------------------

export function parseHtmlToTableData(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const titleEl = doc.querySelector('title');
  const tableName = titleEl
    ? titleEl.textContent.trim()
    : 'Imported Document';

  const container =
    doc.querySelector('main') ||
    doc.querySelector('.container') ||
    doc.body;

  // ---- Gather top-level <details> and preamble ---------------------------
  const topLevelDetails = [];
  const preambleParts = [];

  for (const child of container.children) {
    const tag = child.tagName.toLowerCase();
    if (tag === 'details') {
      topLevelDetails.push(child);
    } else if (
      tag !== 'script' &&
      !(tag === 'div' && child.classList.contains('toolbar'))
    ) {
      if (topLevelDetails.length === 0) {
        const t = child.textContent.trim();
        if (t) preambleParts.push(t);
      }
    }
  }

  // ---- Fallback: heading-based parsing -----------------------------------
  if (topLevelDetails.length === 0) {
    const headingTree = buildHeadingTree(container);
    if (headingTree.length === 0) {
      const text = container.textContent.trim();
      return {
        id: genId(),
        name: tableName,
        rows: [[text || null]],
        num_rows: 1,
        num_cols: 1,
        row_subtables: {},
        column_widths: [500],
        row_heights: null,
        row_header_width: null,
        show_headers: false,
        column_headers: null,
        show_row_headers: false,
        row_headers: null,
        sort_column: null,
        sort_direction: null,
        keep_done_at_bottom: false,
      };
    }

    const topSections =
      headingTree.length === 1 ? headingTree[0].children : headingTree;
    const useRootTitle =
      headingTree.length === 1 ? headingTree[0].title : tableName;

    const rows = [];
    const rowHeaders = [];
    const rowSubtables = {};

    const preambleEls = [];
    for (const el of container.children) {
      if (/^h[1-6]$/.test(el.tagName.toLowerCase())) break;
      if (el.tagName.toLowerCase() !== 'script' && !el.classList.contains('toolbar')) {
        preambleEls.push(el);
      }
    }
    const preamble = preambleEls.map(el => el.textContent.trim()).filter(Boolean).join('\n');

    for (let i = 0; i < topSections.length; i++) {
      const sec = topSections[i];
      rowHeaders.push(sec.title);

      const tempDiv = doc.createElement('div');
      for (const el of sec.contentEls) {
        tempDiv.appendChild(el.cloneNode(true));
      }

      const { cellText, subtables: directSubtables } = buildSectionSubtables(tempDiv);
      rows.push([cellText || null]);

      const childSubtables = sec.children.map(child =>
        headingSectionToSubtable(child, doc)
      );
      const allSubtables = [...directSubtables, ...childSubtables];

      if (allSubtables.length > 0) {
        rowSubtables[`row-${i}`] = allSubtables;
      }
    }

    return {
      id: genId(),
      name: useRootTitle,
      description: preamble || null,
      rows: rows.length > 0 ? rows : [[null]],
      num_rows: Math.max(rows.length, 1),
      num_cols: 1,
      row_subtables: rowSubtables,
      column_widths: [500],
      row_heights: null,
      row_header_width: 250,
      show_headers: true,
      column_headers: ['Content'],
      show_row_headers: true,
      row_headers: rowHeaders.length > 0 ? rowHeaders : ['Section 1'],
      sort_column: null,
      sort_direction: null,
      keep_done_at_bottom: false,
    };
  }

  // ---- Details-based parsing ---------------------------------------------
  const preamble = preambleParts.join('\n');

  // When there's exactly ONE top-level <details> whose body contains child
  // <details> elements, "unwrap" it: use its title as the table name and
  // promote the child details to rows. This handles the common pattern of a
  // single lvl-1 wrapper around many lvl-2 sections.
  let effectiveDetails = topLevelDetails;
  let effectiveTableName = tableName;

  if (topLevelDetails.length === 1) {
    const wrapper = topLevelDetails[0];
    const wrapperBody =
      wrapper.querySelector(':scope > .section-body') || wrapper;
    const childDetails = Array.from(
      wrapperBody.querySelectorAll(':scope > details')
    );

    if (childDetails.length > 0) {
      // Use the wrapper's summary as the table name
      const wrapperSummary = wrapper.querySelector(':scope > summary');
      if (wrapperSummary) {
        effectiveTableName = wrapperSummary.textContent.trim();
      }
      effectiveDetails = childDetails;
    }
  }

  const rows = [];
  const rowHeaders = [];
  const rowSubtables = {};

  for (let i = 0; i < effectiveDetails.length; i++) {
    const detail = effectiveDetails[i];
    const summary = detail.querySelector(':scope > summary');
    const sectionBody =
      detail.querySelector(':scope > .section-body') || detail;
    const sectionName = summary
      ? summary.textContent.trim()
      : `Section ${i + 1}`;

    rowHeaders.push(sectionName);

    // Build cell text (paragraphs only) and subtables (lists, tables, child details)
    const { cellText, subtables } = buildSectionSubtables(sectionBody);
    rows.push([cellText || null]);

    if (subtables.length > 0) {
      rowSubtables[`row-${i}`] = subtables;
    }
  }

  return {
    id: genId(),
    name: effectiveTableName,
    description: preamble || null,
    rows,
    num_rows: rows.length,
    num_cols: 1,
    row_subtables: rowSubtables,
    column_widths: [500],
    row_heights: null,
    row_header_width: 250,
    show_headers: true,
    column_headers: ['Content'],
    show_row_headers: true,
    row_headers: rowHeaders,
    sort_column: null,
    sort_direction: null,
    keep_done_at_bottom: false,
  };
}
